import type React from 'react';

import { Button, Card, CardBody, CardFooter, ChangeDot, Image, Text } from '@change/ds-core-ui';
import { CloseIcon } from '@change/ds-icons';

import { Translate, TranslatePlural, useI18n } from '@change-corgi/core-react/i18n';

import { FEATURED_PETITION_SLUG } from '../../shared/constants';

type Props = Readonly<{
	displayTitle: string;
	// The featured petition's thumbnail; omitted from the toast when null or still processing.
	photo: { url: string; processing: boolean } | null;
	// Exact signatures in the trailing 1-day window (the momentum stat).
	signatureCount: number;
	onDismiss: () => void;
	onCtaClick: () => void;
}>;

/**
 * Renders the UK surging petition toast card.
 *
 * Uses z-28: below petition-details' sticky sign bar and native-share header (z-29), so those stay on
 * top where they overlap the toast.
 */
export const ToastCard: React.FC<Props> = ({ displayTitle, photo, signatureCount, onDismiss, onCtaClick }) => {
	const { translate, localizeNumber } = useI18n();
	const showThumbnail = photo !== null && !photo.processing;

	return (
		<div
			className="fixed bottom-0 left-0 z-28 w-full md:right-4 md:bottom-4 md:left-auto md:w-auto md:max-w-sm"
			data-qa="uk-surging-petition-toast"
		>
			<Card elevation className="bg-background-base">
				<CardBody className="space-y-2">
					<div className="flex items-center gap-2">
						<ChangeDot size="small" />
						<Text size="small" className="text-typography-brand">
							<Translate value="corgi.uk_surging_petition_toast.momentum_label" />
						</Text>
					</div>
					<div className="flex gap-3">
						{showThumbnail && (
							<Image
								// decorative — the adjacent title carries the petition name
								alt=""
								src={photo.url}
								aspectRatio="16:9"
								className="w-20 shrink-0 rounded-md object-cover"
							/>
						)}
						<div className="min-w-0 space-y-1">
							<Text variant="heading" size="small" fontWeight="bold" className="text-typography-strong line-clamp-2">
								{displayTitle}
							</Text>
							<Text size="small" className="text-typography-weak">
								<TranslatePlural
									value="corgi.uk_surging_petition_toast.signed_last_day"
									count={signatureCount}
									replacements={{ countLocalized: localizeNumber(signatureCount) }}
								/>
							</Text>
						</div>
					</div>
				</CardBody>
				<CardFooter className="flex items-center justify-between gap-2">
					<Button
						as="a"
						href={`/p/${FEATURED_PETITION_SLUG}`}
						variant="secondary"
						size="small"
						onClick={onCtaClick}
						data-qa="uk-surging-petition-toast-cta"
					>
						<Translate value="corgi.uk_surging_petition_toast.cta_label" />
					</Button>
					<Button
						iconOnly
						variant="tertiary"
						size="small"
						leadingIcon={<CloseIcon size="small" />}
						aria-label={translate('corgi.uk_surging_petition_toast.dismiss_label')}
						onClick={onDismiss}
						data-qa="uk-surging-petition-toast-dismiss"
					/>
				</CardFooter>
			</Card>
		</div>
	);
};
